// SPDX-FileCopyrightText: 2025 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { LabelPackageInfoMessage } from '@tls/sw91-communication/types/com.api_db_access';
import DatasetTag from 'components/common/DatasetTag';
import ProcessTag from 'components/common/ProcessTag';
import { DatasetFilterType, QCResult } from 'components/project/data/filter/filters-defaults';
import IconImage from 'components/treactui-template/icons/IconImage';
import IconLabel from 'components/treactui-template/icons/IconLabel';
import useLabelSummary from 'hooks/useLabelSummary';
import React from 'react';
import styled from 'styled-components';
import { Colors, OutputFooter } from '@tls/treact-ui';
import { IconCircle } from '@tls/treact-ui/icons';

interface Props {
  labels: LabelPackageInfoMessage[];
  isQC: boolean;
}

export default function ImageListFooter({ labels, isQC }: Readonly<Props>) {
  const labelSummary = useLabelSummary(labels);

  return (
    <Root data-testid='label-images-summary-footer'>
      <Item data-testid='label-images-summary-total'>
        <IconImage height={'20px'} />
        <Amount>{labels.length}</Amount>
      </Item>
      {isQC && (
        <>
          <div data-testid='label-images-summary-training-set'>
            <DatasetTag size='20px' dataset={DatasetFilterType.TrainingSet} />
            <Amount>{labelSummary.traingSetCount}</Amount>
          </div>
          <div data-testid='label-images-summary-evaluation-set'>
            <DatasetTag size='20px' dataset={DatasetFilterType.EvaluationSet} />
            <Amount>{labelSummary.evaluationSetCount}</Amount>
          </div>
        </>
      )}
      {!isQC && (
        <div data-testid='label-images-summary-labeled'>
          <IconLabel height={'20px'} />
          <Amount>{labelSummary.evaluationSetCount + labelSummary.traingSetCount}</Amount>
        </div>
      )}
      <div data-testid='label-images-summary-unlabeled'>
        <IconCircle strokeWidth='2px' viewBox='-2 -2 40 40' height={'20px'} />
        <Amount>{labelSummary.unlabeldCount}</Amount>
      </div>
      {isQC && (
        <>
          <Divider />
          <div data-testid='label-images-summary-qc-good'>
            <ProcessTag size={'20px'} processTag={QCResult.Good} />
            <Amount>{labelSummary.goodProcessCount}</Amount>
          </div>
          <div data-testid='label-images-summary-qc-bad'>
            <ProcessTag size={'20px'} processTag={QCResult.Bad} />
            <Amount>{labelSummary.badProcessCount}</Amount>
          </div>
          <div data-testid='label-images-summary-qc-undefined'>
            <ProcessTag size={'20px'} processTag={QCResult.Undefined} />
            <Amount>{labelSummary.undefinedProcessCount}</Amount>
          </div>
        </>
      )}
    </Root>
  );
}

const Root = styled(OutputFooter)`
  height: 4rem;
  display: flex;
  width: 100%;
  justify-content: end;
  z-index: 100;
`;
const Item = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
`;
const Amount = styled.div`
  text-align: center;
`;

const Divider = styled.div`
  height: calc(100% - 0.5rem);
  width: 1px;
  background-color: ${Colors.trgrey2.hex};
`;
